import { useEffect, useMemo, useState } from "react";

const TICK_INTERVAL_MS = 60_000;

const formatHHMM = (totalMinutes: number): string => {
	const hh = Math.floor(totalMinutes / 60);
	const mm = totalMinutes % 60;

	return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const computeDisplay = (orderUntil: string | null | undefined): string | null => {
	if (!orderUntil) return null;
	const target = Date.parse(orderUntil);

	if (Number.isNaN(target)) return null;

	const remainingMs = target - Date.now();
	const remainingMin = Math.max(0, Math.ceil(remainingMs / TICK_INTERVAL_MS));

	return formatHHMM(remainingMin);
};

const useDiningTimerHHMM = (orderUntil: string | null | undefined): string | null => {
	const [tickKey, setTickKey] = useState(0);

	const display = useMemo(
		() => computeDisplay(orderUntil),
		// tickKey forces recompute when interval ticks; eslint-disable for intentional dep
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[orderUntil, tickKey]
	);

	useEffect(() => {
		if (!orderUntil) return;
		const target = Date.parse(orderUntil);

		if (Number.isNaN(target) || target <= Date.now()) return;

		let intervalId: ReturnType<typeof setInterval> | null = null;

		const incrementTick = () => setTickKey((k) => k + 1);

		const stop = () => {
			if (intervalId !== null) {
				clearInterval(intervalId);
				intervalId = null;
			}
		};

		const onIntervalTick = () => {
			incrementTick();
			if (Date.now() >= target) stop();
		};

		const start = () => {
			if (intervalId !== null) return;
			intervalId = setInterval(onIntervalTick, TICK_INTERVAL_MS);
		};

		const handleVisibilityChange = () => {
			if (document.hidden) {
				stop();

				return;
			}
			incrementTick();
			if (Date.now() < target) start();
		};

		start();
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			stop();
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [orderUntil]);

	return display;
};

export default useDiningTimerHHMM;
